import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { IconButton, Menu, MenuItem, Tooltip, ListItemIcon, ListItemText, Badge } from '@mui/material';
import Icon from '@mui/material/Icon';

import DialogOrganisatieActivate from '../../controls/dialogs/DialogOrganisatieActivate';

import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { userManagementService } from '../../../services/user-management.service';
import { authService } from '../../../services/auth.service';


const MijnOrganisatiesNavMenu: React.FC = () => {
    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [mijnOrganisaties, setMijnOrganisaties] = useState<MijnOrganisatieListItem[]>([]);
    const [selectedOrganisatie, setSelectedOrganisatie] = useState<MijnOrganisatieListItem>();
    const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem>();
    const [open, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const openMenu = Boolean(anchorEl);
    const hasMijnOrganisatieData = useRef(false);

    useEffect(() => {
        getStoredOrganisatie();

        // Ensure the fetch happens only once
        if (!hasMijnOrganisatieData.current) {
            fetchMijnOrganisaties();
            hasMijnOrganisatieData.current = true; // Mark as fetched
        }
    }, []); // Empty dependency array ensures this runs once when the component mounts

    const getStoredOrganisatie = async () => {
        const organisatie = userManagementService.getStoredOrganisatie();
        if (organisatie) {
            setMijnOrganisatie(organisatie);
        }
    };

    const fetchMijnOrganisaties = async () => {
        setLoading(true);
        try {
            const items = await userManagementService.mijnOrganisaties();
            if (items) {
                if (items.length === 1 && !mijnOrganisatie) {
                    const storedOrganisatie = userManagementService.getStoredOrganisatie();
                    if (!storedOrganisatie) {
                        const organisatie = items[0] as MijnOrganisatieListItem;
                        setSelectedOrganisatie(organisatie);
                        userManagementService.setStoredOrganisatie(organisatie);
                        setMijnOrganisatie(organisatie);
                        window.location.reload(); // Forces a full page reload
                    }
                    else {
                        setMijnOrganisatie(storedOrganisatie);
                    }
                }
                const sortedItems = [...items].sort((a, b) => a.displayName.localeCompare(b.displayName));
                setMijnOrganisaties(sortedItems); // Assuming the API response is a JSON object containing array of items
            }

            setLoading(false);
        }
        catch (error: any) {
            // If token refresh failed, redirect to the logout page
            if (error.message === 'Unable to refresh token') {
                navigate('/verlopen');
            }
            setError(error.message || 'Er is een fout opgetreden');
            setLoading(false);
        }
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSelectOrganisatie = (organisatie: MijnOrganisatieListItem) => {
        setSelectedOrganisatie(organisatie)
        openDialog();
    };

    const openDialog = () => {
        setOpenDialog(true);
        handleCloseMenu();
    };

    const closeDialog = () => {
        setOpenDialog(false);
    };

    const handleConfirmSimulation = () => {
        userManagementService.setStoredOrganisatie(selectedOrganisatie);
        closeDialog();
        setMijnOrganisatie(selectedOrganisatie);
        // navigate(0);
        window.location.reload(); // window.location.replace('/plannen'); // Forces a full page reload
    }

    const onClickNavigate = (path: string) => {
        if (path.startsWith('http')) {
            window.open(path, '_blank', 'noopener,noreferrer');
        } 
        else {
            navigate(path);
        }
        handleCloseMenu();
    };

    return (
        <div>
            {mijnOrganisaties && mijnOrganisaties.length > 1 && (
                <>
                    <Tooltip title={
                            mijnOrganisatie
                            ? `Actieve organisatie: ${mijnOrganisatie.displayName}. Wil je een andere gebruiken kies dan een andere organisatie.`
                            : "Kies welke organisatie je wilt gebruiken"
                        }>
                        <IconButton
                            onClick={handleMenuClick}
                            className="btn-nav-header btn-organisatie-menu"
                            size="large"
                            sx={{
                                ml: 2,
                                ...(mijnOrganisatie && {
                                    // backgroundColor: 'rgba(124, 91, 174, 0.05) !important',
                                    // '&:hover': {
                                    //     backgroundColor: 'rgba(124, 91, 174, 0.2) !important',
                                    // },

                                    backgroundColor: 'rgba(46, 125, 66, 0.05) !important',
                                    '&:hover': {
                                        backgroundColor: 'rgba(46, 125, 66, 0.2) !important',
                                    },
                                }),
                            }}
                            aria-haspopup="true">
                            {mijnOrganisatie ? (
                                <Badge
                                    variant="dot"
                                    overlap="circular"
                                    anchorOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            backgroundColor: 'rgb(46, 125, 66);', // Paars rgb(124, 91, 174)
                                            color: 'rgb(46, 125, 66);',
                                            top: -4,
                                            right: -4,
                                        }
                                    }}
                                    >
                                    <Icon>business</Icon>
                                </Badge>
                            ) : (
                                <Icon>business</Icon>
                            )}
                        </IconButton>
                    </Tooltip>
                    <Menu
                        id="organisatie-menu"
                        className="navigation nav-menu"
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleCloseMenu}
                        slotProps={{
                            paper: {
                                elevation: 0,
                                sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                '& .MuiAvatar-root': {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                                '&::before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                        {mijnOrganisaties.map((organisatie) =>
                            (mijnOrganisatie && mijnOrganisatie.id === organisatie.id) ? (
                                <Tooltip title={`${mijnOrganisatie.displayName} beheren`} key={organisatie.id}>
                                    <MenuItem
                                        style={{ backgroundColor: '#d4edda', color: '#2E7D42' }}
                                        onClick={() => onClickNavigate(`/mijn/organisatie/${organisatie.id}`)}>
                                        <ListItemIcon>
                                            <Icon style={{ color: '#2E7D42' }}>check_circle</Icon>
                                        </ListItemIcon>
                                        <ListItemText>{organisatie.displayName}</ListItemText>
                                    </MenuItem>
                                </Tooltip>
                            ) : (
                                <MenuItem key={organisatie.id} onClick={() => handleSelectOrganisatie(organisatie)}>
                                    <ListItemIcon>
                                        <Icon>domain</Icon>
                                    </ListItemIcon>
                                    {/* <ListItemText>{organisatie.displayName}</ListItemText> */}
                                    {organisatie.displayName}
                                </MenuItem>
                            )
                        )}
                    </Menu>

                    {selectedOrganisatie && selectedOrganisatie.displayName && (
                        <DialogOrganisatieActivate
                            open={open}
                            organisatieNaam={selectedOrganisatie.displayName.toString()}
                            onClose={() => closeDialog()}
                            onConfirm={handleConfirmSimulation}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default MijnOrganisatiesNavMenu;
